/**
 * Class for logging all of the things:
 * user events, analytics data, errors, and more.
 */
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'

class TrackEDU {

    /**
     * Configure settings.
     * @param {Object} opts - Options.
     * @param {Object} opts.history - history object from react-router-dom
     * @param {Number} opts.tracesSampleRate - rate (from 0 to 1) at which traces will be recorded/sent to Sentry
     *
     */
    constructor(opts = {}) {
        // Class wide settings
        this.opts = this._validateOpts({ ...this.defaults, ...opts })
        this.env = process.env.CONFIG_FILE || 'local'
        this.profile = {}
        this._init()
    }

    get defaults() {
        return this.constructor.defaults
    }

    get options() {
        return this.opts
    }

    /**
     * Validate that all required options have been provided.
     * @private
     */
    _validateOpts(opts) {
        if (opts && isNaN(opts.tracesSampleRate)) throw new Error('TrackEDU - invalid format for option tracesSampleRate')
        if (opts && !opts.history) throw new Error('TrackEDU - requires history option')
        return opts
    }

    /**
     * Let's get cookin'.
     * @private
     */
    _init() {
        // TODO: would be nice if this could also load Raven from CDN or node modules
        Sentry.init({
            dsn: 'https://80f0cb34c87148c1ba043826f5adb539@o187110.ingest.sentry.io/2771648',
            release: process.env.SENTRY_RELEASE || 'local',
            environment: this.env,
            debug: this.env !== 'production',
            integrations: [new BrowserTracing({
                routingInstrumentation: Sentry.reactRouterV5Instrumentation(this.opts.history),
            })],

            tracesSampleRate: this.opts.tracesSampleRate,
        });
    }

    /**
     * Register and update profile data on the current user.
     * @public
     * @param {string} id - Unique identifier for the user.
     * @param {Object} [data] - Optional. Any arbitrary user data you wish to provide.
     * @returns {TrackEDU} - `this`
     */
    identify(id, data = {}) {
        if (!id) throw new Error('TrackEDU - identify() method missing param: id.')
        this.profile = {
            ...this.profile,
            ...data,
            id
        }
        Sentry.configureScope(scope => {
            scope.setUser({ ...this.profile })
        });
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Record a pageview.
     * @public
     * @param {string} path - Path of the page to register a pageview for.
     * @returns {TrackEDU} - `this`
     */
    page(path) {
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Fire tracking events.
     * @public
     * @param {string} name - Name of the event to track.
     * @param {Object} [data] - Additional payload to include in the tracking event.
     * @returns {TrackEDU} - `this`
     */
    track(name, data = {}) {
        const event = {
            'event': name,
            ...data
        }
        // TODO: Wrap corresponding segment.io method
        return this
    }

    /**
     * Log arbitrary application activity.
     * @public
     * @param {string} message - Message to log.
     * @param {Object} [data] - Optional. Additional data to include with the entry.
     * @returns {TrackEDU} - `this`
     */
    log(message, data = {}) {
        if (this.env === 'production') {
            Sentry.withScope(scope => {
                Object.keys(data).forEach((extraItemKey) => {
                    scope.setExtra(extraItemKey, data[extraItemKey])
                })
                Sentry.captureMessage(message, 'info');
            });
        } else {
            console.log(message, data)
        }
        return this
    }

    /**
     * Log arbitrary application warnings.
     * @public
     * @param {string} message - Message to log.
     * @param {Object} [data] - Optional. Additional data to include with the entry.
     * @returns {TrackEDU} - `this`
     */
    warn(message, data = {}) {
        console.log({ message, data })
        if (this.env === 'production') {
            Sentry.withScope(scope => {
                Object.keys(data).forEach((extraItemKey) => {
                    scope.setExtra(extraItemKey, data[extraItemKey])
                })
                Sentry.captureMessage(message, 'warning');
            });
        } else {
            console.warn(message, data)
        }
        return this
    }

    /**
     * Log an application error.
     * @public
     * @param {Object} error - Thrown error to log.
     * @param {string} message - Error message to log.
     * @param {Object} [data] - Optional. Additional data to include with error.
     * @returns {TrackEDU} - `this`
     */
    error(error, message, data = {}) {
        if (this.env !== 'production') console.error(message, error, data)

        Sentry.withScope(scope => {
            scope.setExtra('message', message)
            Object.keys(data).forEach((extraItemKey) => {
                scope.setExtra(extraItemKey, data[extraItemKey])
            })
            Sentry.captureException(error)
        });
        return this
    }

    /**
     * Reset the tracker's state.
     * @public
     * @returns {TrackEDU} - `this`
     */
    reset() {
        Sentry.configureScope(scope => {
            scope.clear()
        });
        this.profile = {}
        return this
    }

}

TrackEDU.defaults = {
    tracesSampleRate: 0
}


export default TrackEDU
